import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { toast, confirmar } from '../lib/toast';
import { fmt } from '../lib/formatters';
import { Card } from './ui/Card';
import { Spinner } from './ui/Spinner';
import { EtiquetaFotoProveedor, useHeredadoProveedor, type HeredadoProveedor } from './producto/HeredadoProveedor';

type TipoArchivo = 'manual_pdf' | 'packaging_pdf' | 'foto' | 'specs' | 'user_guide' | 'etiqueta_energetica' | 'otros';

type ArchivoRow = {
  id: string;
  tipo: TipoArchivo;
  storage_path: string;
  nombre_original: string;
  mime_type: string | null;
  bytes: number | null;
  ordering: number;
  created_at: string;
  url: string | null;
};

type ListResponse = {
  data: ArchivoRow[];
  storage_configured: boolean;
};

// 4 secciones de documentos (foto va aparte arriba en ProductoDetalle).
// Packaging acepta PDF y Word; resto según corresponda.
const SECCIONES: Array<{ tipo: TipoArchivo; titulo: string; accept: string }> = [
  { tipo: 'manual_pdf',    titulo: 'Manual',           accept: 'application/pdf,.pdf' },
  { tipo: 'packaging_pdf', titulo: 'Packaging',        accept: '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  { tipo: 'specs',         titulo: 'SPECS',            accept: '.xls,.xlsx,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  { tipo: 'user_guide',    titulo: 'User Guide RAW',   accept: '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
];

// Solo en monitores (o si el producto ya tiene una subida).
const SECCION_ETIQUETA = { tipo: 'etiqueta_energetica' as const, titulo: 'Etiqueta energética', accept: '.pdf,application/pdf,image/*' };

function esMonitor(categoriaNombre: string | null | undefined): boolean {
  return (categoriaNombre ?? '').trim().toUpperCase() === 'MONITOR';
}

function tamano(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

async function uploadFile(productoId: string, tipo: TipoArchivo, file: File): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  const fd = new FormData();
  fd.append('tipo', tipo);
  fd.append('file', file);
  const res = await fetch(`/api/productos/${productoId}/archivos`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Upload falló (${res.status}): ${txt}`);
  }
}

async function deleteFile(productoId: string, archivoId: string): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  const res = await fetch(`/api/productos/${productoId}/archivos/${archivoId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Delete falló (${res.status}): ${txt}`);
  }
}

// ───────────────────────────────────────────────────────────────────────
// Iconos
// ───────────────────────────────────────────────────────────────────────

function FileIcon({ tipo, mime, hasFile }: { tipo: TipoArchivo; mime: string | null; hasFile: boolean }) {
  // Color y letra según mime
  const ext = (() => {
    if (!mime) return tipo === 'specs' ? 'XLS' : tipo === 'user_guide' ? 'DOC' : 'PDF';
    if (mime.includes('pdf')) return 'PDF';
    if (mime.includes('word') || mime.includes('msword') || mime.includes('officedocument.wordprocessingml')) return 'DOC';
    if (mime.includes('excel') || mime.includes('spreadsheetml')) return 'XLS';
    return mime.split('/')[1]?.slice(0, 3).toUpperCase() || 'FILE';
  })();
  const colors: Record<string, string> = {
    PDF: 'bg-rose-500',
    DOC: 'bg-sky-600',
    XLS: 'bg-emerald-600',
    FILE: 'bg-slate-400',
  };
  const fill = hasFile ? (colors[ext] ?? colors.FILE) : 'bg-slate-200';
  const text = hasFile ? 'text-white' : 'text-slate-400';
  return (
    <div className="relative">
      <svg viewBox="0 0 64 80" className={`h-16 w-12 ${text}`} fill="currentColor">
        <path
          className={fill}
          d="M8 0h36l16 16v60a4 4 0 01-4 4H8a4 4 0 01-4-4V4a4 4 0 014-4z"
          fill="currentColor"
        />
        <path d="M44 0v12a4 4 0 004 4h12L44 0z" fill="rgba(255,255,255,0.25)" />
      </svg>
      <span className={`absolute inset-x-0 bottom-2 text-center text-[10px] font-bold tracking-wider ${hasFile ? 'text-white' : 'text-slate-500'}`}>
        {ext}
      </span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Card de cada tipo
// ───────────────────────────────────────────────────────────────────────

function DocCard({
  tipo,
  titulo,
  accept,
  archivo,
  uploading,
  onUpload,
  onDelete,
}: {
  tipo: TipoArchivo;
  titulo: string;
  accept: string;
  archivo: ArchivoRow | undefined;
  uploading: boolean;
  onUpload: (file: File) => void;
  onDelete: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasFile = !!archivo;

  return (
    <div className="flex flex-col items-center rounded-md border border-slate-200 bg-white p-3">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">{titulo}</div>
      <button
        type="button"
        disabled={!hasFile || !archivo?.url}
        onClick={() => archivo?.url && window.open(archivo.url, '_blank', 'noopener')}
        className={`mb-2 ${hasFile && archivo?.url ? 'cursor-pointer hover:scale-105 transition' : 'cursor-default'}`}
        title={hasFile ? `Abrir ${archivo!.nombre_original}` : 'Sin archivo'}
      >
        <FileIcon tipo={tipo} mime={archivo?.mime_type ?? null} hasFile={hasFile} />
      </button>
      {hasFile && (
        <div className="mb-2 w-full truncate text-center text-[11px] text-slate-500" title={archivo!.nombre_original}>
          {archivo!.nombre_original}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
      <div className="flex gap-1">
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="cursor-pointer rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
        >
          {uploading ? 'Subiendo…' : hasFile ? 'Reemplazar' : 'Subir'}
        </button>
        {hasFile && (
          <button
            type="button"
            onClick={async () => {
              if (await confirmar({ title: `¿Eliminar ${titulo.toLowerCase()}?`, confirmLabel: 'Eliminar', danger: true })) onDelete();
            }}
            className="cursor-pointer rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
          >
            Eliminar
          </button>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Componente principal — fila horizontal con las 4 secciones de documentos
// ───────────────────────────────────────────────────────────────────────

/**
 * «Otros DOCS»: cajón para cualquier documento que no encaja en las secciones
 * fijas. Varios archivos, de cualquier tipo; cada uno se abre o se borra suelto.
 */
function OtrosDocs({ archivos, storageReady, uploading, onUpload, onDelete }: {
  archivos: ArchivoRow[];
  storageReady: boolean;
  uploading: boolean;
  onUpload: (files: File[]) => void;
  onDelete: (a: ArchivoRow) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Otros DOCS</div>
        <button
          type="button"
          disabled={uploading || !storageReady}
          onClick={() => inputRef.current?.click()}
          className="cursor-pointer rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
        >
          {uploading ? 'Subiendo…' : '+ Añadir'}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            onUpload(Array.from(e.target.files ?? []));
            if (inputRef.current) inputRef.current.value = '';
          }}
        />
      </div>
      {archivos.length === 0 ? (
        <p className="text-xs text-slate-400">Sin documentos. Aquí va cualquier otro archivo del producto: certificados, declaraciones de conformidad, fichas de seguridad…</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {archivos.map((a) => (
            <li key={a.id} className="flex items-center gap-3 py-1.5 text-sm">
              <a
                href={a.url ?? undefined}
                target="_blank"
                rel="noreferrer"
                className={`min-w-0 flex-1 truncate ${a.url ? 'text-accent-700 underline' : 'text-slate-500'}`}
                title={a.nombre_original}
              >
                {a.nombre_original}
              </a>
              <span className="shrink-0 text-xs tabular-nums text-slate-400">{tamano(a.bytes)} · {fmt.date(a.created_at)}</span>
              <button
                type="button"
                onClick={async () => {
                  if (await confirmar({ title: `¿Eliminar "${a.nombre_original}"?`, confirmLabel: 'Eliminar', danger: true })) onDelete(a);
                }}
                className="shrink-0 cursor-pointer rounded-md border border-rose-200 px-2 py-0.5 text-xs text-rose-600 hover:bg-rose-50"
              >
                Eliminar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ProductoArchivos({ productoId, categoriaNombre }: { productoId: string; categoriaNombre?: string | null }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['producto-archivos', productoId],
    queryFn: async () => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const res = await fetch(`/api/productos/${productoId}/archivos`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`API archivos → ${res.status}`);
      return (await res.json()) as ListResponse;
    },
  });

  const upload = useMutation({
    mutationFn: ({ tipo, file }: { tipo: TipoArchivo; file: File }) => uploadFile(productoId, tipo, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto-archivos', productoId] }),
    onError: (err) => toast.error('No se pudo subir el archivo', (err as Error).message),
  });

  // Otros DOCS admite varios a la vez: en serie, para no saturar el server.
  const uploadOtros = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) await uploadFile(productoId, 'otros', f);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['producto-archivos', productoId] }),
    onError: (err) => toast.error('No se pudo subir el archivo', (err as Error).message),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteFile(productoId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto-archivos', productoId] }),
    onError: (err) => toast.error('No se pudo borrar el archivo', (err as Error).message),
  });

  if (query.isLoading) return <Card title="Documentos del producto"><Spinner /></Card>;
  const list = query.data?.data ?? [];
  const storageReady = query.data?.storage_configured ?? false;

  // Para los 4 tipos de documento agarro el más reciente como "el archivo
  // actual" — el flujo de "Reemplazar" sustituye al previo subiendo uno
  // nuevo (mantenemos historial en BD pero mostramos sólo el último).
  const latestByTipo = new Map<TipoArchivo, ArchivoRow>();
  for (const a of list) {
    if (a.tipo === 'foto' || a.tipo === 'otros') continue;
    const prev = latestByTipo.get(a.tipo as TipoArchivo);
    if (!prev || a.created_at > prev.created_at) {
      latestByTipo.set(a.tipo as TipoArchivo, a);
    }
  }
  const secciones = esMonitor(categoriaNombre) || latestByTipo.has('etiqueta_energetica')
    ? [...SECCIONES, SECCION_ETIQUETA]
    : SECCIONES;
  const otros = list
    .filter((a) => a.tipo === 'otros')
    .sort((a, b) => a.created_at.localeCompare(b.created_at));

  return (
    <Card
      title="Documentos del producto"
      description={storageReady
        ? 'Click en el icono para abrir. Reemplazar sustituye el archivo anterior. Las imágenes >2MB se comprimen automáticamente.'
        : 'Storage aún no configurado. Los archivos no se pueden subir hasta que el server tenga SUPABASE_SERVICE_ROLE_KEY.'}
    >
      <div className={`grid grid-cols-2 gap-3 ${secciones.length > 4 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
        {secciones.map((s) => {
          const archivo = latestByTipo.get(s.tipo);
          return (
            <DocCard
              key={s.tipo}
              tipo={s.tipo}
              titulo={s.titulo}
              accept={s.accept}
              archivo={archivo}
              uploading={upload.isPending && upload.variables?.tipo === s.tipo}
              onUpload={(file) => {
                if (!storageReady) return;
                // Si ya hay un archivo, lo eliminamos antes de subir el nuevo
                // para no acumular versiones huérfanas.
                const prev = latestByTipo.get(s.tipo);
                if (prev) del.mutate(prev.id);
                upload.mutate({ tipo: s.tipo, file });
              }}
              onDelete={() => archivo && del.mutate(archivo.id)}
            />
          );
        })}
      </div>
      <OtrosDocs
        archivos={otros}
        storageReady={storageReady}
        uploading={uploadOtros.isPending}
        onUpload={(files) => { if (storageReady && files.length && !uploadOtros.isPending) uploadOtros.mutate(files); }}
        onDelete={(a) => del.mutate(a.id)}
      />
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Galería de fotos — se renderiza por separado (en la cabecera del producto)
// ───────────────────────────────────────────────────────────────────────

// Imágenes de un DataTransfer (arrastrar o portapapeles). Las pegadas llegan
// como "image.png" genérico: se renombran para distinguirlas en el listado.
function imagenesDe(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const files = Array.from(dt.files).filter((f) => f.type.startsWith('image/'));
  return files.map((f, i) => {
    if (f.name && f.name !== 'image.png') return f;
    const ext = f.type.split('/')[1] || 'png';
    return new File([f], `pegada-${Date.now()}${files.length > 1 ? `-${i + 1}` : ''}.${ext}`, { type: f.type });
  });
}

/**
 * Fotos heredadas del producto de proveedor (solo lectura): se ven mientras el
 * producto no tenga ninguna propia, con la etiqueta «Foto Proveedor».
 */
function FotosProveedor({ fotos, origen }: { fotos: HeredadoProveedor['fotos']; origen: HeredadoProveedor['producto_proveedor'] }) {
  const [elegida, setElegida] = useState<string | null>(null);
  const principal = fotos.find((f) => f.id === elegida) ?? fotos[0];
  const miniaturas = fotos.filter((f) => f.id !== principal.id);
  return (
    <div>
      <div className="relative overflow-hidden rounded-md border border-amber-200">
        <a href={principal.url} target="_blank" rel="noreferrer">
          <img src={principal.url} alt={principal.nombre ?? 'Foto del proveedor'} className="aspect-square w-full bg-white object-contain p-1" />
        </a>
        <EtiquetaFotoProveedor className="absolute left-1.5 top-1.5" />
      </div>
      {miniaturas.length > 0 && (
        <div className="mt-2 grid grid-cols-4 gap-1.5">
          {miniaturas.map((m) => (
            <button key={m.id} type="button" onClick={() => setElegida(m.id)} title="Ver en grande" className="block w-full cursor-pointer">
              <img src={m.url} alt={m.nombre ?? 'Foto del proveedor'} className="aspect-square w-full rounded-md border border-amber-200 bg-white object-contain p-0.5 hover:border-amber-400" />
            </button>
          ))}
        </div>
      )}
      <p className="mt-1.5 text-[11px] text-slate-500">
        Del producto de proveedor{origen ? ` ${origen.ref_proveedor}${origen.proveedor_nombre ? ` (${origen.proveedor_nombre})` : ''}` : ''}. Al subir una foto propia deja de verse.
      </p>
    </div>
  );
}

export function ProductoFotos({ productoId }: { productoId: string }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const hoverRef = useRef(false);
  const [arrastrando, setArrastrando] = useState(false);

  const query = useQuery({
    queryKey: ['producto-archivos', productoId],
    queryFn: async () => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const res = await fetch(`/api/productos/${productoId}/archivos`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`API archivos → ${res.status}`);
      return (await res.json()) as ListResponse;
    },
  });

  // En serie: el orden de subida es el orden de las fotos (created_at).
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) await uploadFile(productoId, 'foto', f);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['producto-archivos', productoId] }),
    onError: (err) => toast.error('No se pudo subir el archivo', (err as Error).message),
  });
  const subir = (files: File[]) => {
    if (files.length && !upload.isPending) upload.mutate(files);
  };

  // Pegar (Ctrl+V) con el ratón sobre el cuadro o con el foco dentro. Se
  // escucha en window porque un div sin foco no recibe el evento paste; no se
  // toca el pegado en un campo de texto que no sea de este cuadro.
  const subirRef = useRef(subir);
  subirRef.current = subir;
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const box = boxRef.current;
      const activo = document.activeElement;
      const dentro = !!box && !!activo && box.contains(activo);
      const enCampo = activo instanceof HTMLInputElement || activo instanceof HTMLTextAreaElement || (activo as HTMLElement | null)?.isContentEditable;
      if (!dentro && (!hoverRef.current || enCampo)) return;
      const files = imagenesDe(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      subirRef.current(files);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  const del = useMutation({
    mutationFn: (id: string) => deleteFile(productoId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto-archivos', productoId] }),
    onError: (err) => toast.error('No se pudo borrar el archivo', (err as Error).message),
  });

  const fotos = (query.data?.data ?? [])
    .filter((a) => a.tipo === 'foto')
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  // Sin fotos propias se enseñan las del producto de proveedor, etiquetadas.
  const heredado = useHeredadoProveedor(productoId, query.isSuccess && fotos.length === 0);

  // La grande es la elegida en las miniaturas (por defecto la primera); se
  // ve entera (object-contain), sin recortar. Debajo, las demás.
  const [elegida, setElegida] = useState<string | null>(null);
  const principal = fotos.find((f) => f.id === elegida) ?? fotos[0] ?? null;
  const miniaturas = fotos.filter((f) => f.id !== principal?.id);

  return (
    <div
      ref={boxRef}
      tabIndex={0}
      title="Arrastra imágenes aquí o pégalas con Ctrl+V"
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; }}
      onDragOver={(e) => {
        if (!Array.from(e.dataTransfer.types).includes('Files')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setArrastrando(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setArrastrando(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setArrastrando(false);
        const files = imagenesDe(e.dataTransfer);
        if (files.length) subir(files);
        else if (e.dataTransfer.files.length) toast.error('Solo se admiten imágenes');
      }}
      className={`relative rounded-md border bg-white p-3 outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${arrastrando ? 'border-amber-400 ring-2 ring-amber-300' : 'border-slate-200'}`}
    >
      {arrastrando && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md bg-amber-50/90 text-sm font-medium text-amber-800">
          Suelta para añadir las fotos
        </div>
      )}
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Fotos</h3>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
          className="cursor-pointer rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
        >
          {upload.isPending ? 'Subiendo…' : '+ Añadir'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            subir(Array.from(e.target.files ?? []));
            if (inputRef.current) inputRef.current.value = '';
          }}
        />
      </div>

      {fotos.length === 0 ? (
        heredado.data?.fotos.length ? (
          <FotosProveedor fotos={heredado.data.fotos} origen={heredado.data.producto_proveedor} />
        ) : (
          <div className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-slate-200 px-2 text-center text-xs text-slate-400">
            <span>Sin fotos</span>
            <span>Arrastra imágenes aquí o pégalas con Ctrl+V</span>
          </div>
        )
      ) : (
        <div>
          {principal && (
            <div className="group relative overflow-hidden rounded-md border border-slate-200">
              <a href={principal.url ?? '#'} target="_blank" rel="noreferrer">
                <img
                  src={principal.url ?? ''}
                  alt={principal.nombre_original}
                  className="aspect-square w-full bg-white object-contain p-1"
                />
              </a>
              <button
                type="button"
                onClick={async () => {
                  if (await confirmar({ title: `¿Eliminar "${principal.nombre_original}"?`, confirmLabel: 'Eliminar', danger: true })) del.mutate(principal.id);
                }}
                className="absolute right-1 top-1 hidden cursor-pointer rounded bg-white/90 px-1.5 py-0.5 text-xs text-rose-700 shadow group-hover:block"
              >
                ×
              </button>
            </div>
          )}
          {miniaturas.length > 0 && (
            <div className="mt-2 grid grid-cols-4 gap-1.5">
              {miniaturas.map((m) => (
                <div key={m.id} className="group relative">
                  <button type="button" onClick={() => setElegida(m.id)} title="Ver en grande" className="block w-full cursor-pointer">
                    <img
                      src={m.url ?? ''}
                      alt={m.nombre_original}
                      className="aspect-square w-full rounded-md border border-slate-200 bg-white object-contain p-0.5 hover:border-amber-400"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (await confirmar({ title: `¿Eliminar "${m.nombre_original}"?`, confirmLabel: 'Eliminar', danger: true })) del.mutate(m.id);
                    }}
                    className="absolute right-0.5 top-0.5 hidden cursor-pointer rounded bg-white/90 px-1 text-[10px] text-rose-700 shadow group-hover:block"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
